import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getCookie } from '../components/utils/utils';
import { PriceEstimationReport } from '../components/PriceEstimation/PriceEstimationReport';
import './PriceEstimations.scss';
import { formatCardPrice } from '../components/ListingCard/cardUtils';

export interface EstimationListItem {
  _id: string;
  status: 'queued' | 'in_progress' | 'completed' | 'failed' | 'incomplete';
  error_message?: string;
  address: {
    street?: string;
    houseNumber?: string;
    city?: string;
    postcode?: string;
    country?: string;
  };
  property_details?: {
    size?: number;
    rooms?: string;
  };
  result?: {
    estimated_price?: number;
    confidence_level?: string;
  };
  created_at: string;
  completed_at?: string;
}

interface EstimationDetails {
  estimation_id: string;
  address: any;
  property_details: any;
  result: any;
  created_at: string;
  completed_at: string | null;
}

interface PriceEstimationsProps {
  onBack?: () => void;
  /** The profile view draws the header itself; a page mounting this standalone keeps its own. */
  hideHeader?: boolean;
  estimations: EstimationListItem[];
  setEstimations: (estimations: EstimationListItem[]) => void;
  initialEstimationId?: string;
  onOpenEstimationDetail?: (estimationId: string) => void;
  onCloseEstimationDetail?: () => void;
}

const PriceEstimations: React.FC<PriceEstimationsProps> = ({
  onBack,
  hideHeader,
  estimations,
  setEstimations,
  initialEstimationId,
  onOpenEstimationDetail,
  onCloseEstimationDetail,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEstimation, setSelectedEstimation] = useState<EstimationDetails | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [loadingReport, setLoadingReport] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [pollingIntervals, setPollingIntervals] = useState<Map<string, NodeJS.Timeout>>(new Map());
  const openedInitialEstimationIdRef = useRef<string | null>(null);

  useEffect(() => {
    fetchEstimations();
  }, [page, filterStatus]);

  useEffect(() => {
    if (!initialEstimationId || loading || loadingReport || reportModalOpen) {
      return;
    }

    if (openedInitialEstimationIdRef.current === initialEstimationId) {
      return;
    }

    const deepLinkedEstimation = estimations.find((estimation) => estimation._id === initialEstimationId);
    if (!deepLinkedEstimation || deepLinkedEstimation.status !== 'completed') {
      return;
    }

    openedInitialEstimationIdRef.current = initialEstimationId;
    fetchFullReport(initialEstimationId);
  }, [estimations, initialEstimationId, loading, loadingReport, reportModalOpen]);

  // Poll for in-progress estimations on component mount and when estimations change
  useEffect(() => {
    console.log('Checking for in-progress estimations to poll...', estimations);

    // Clear any existing intervals first
    setPollingIntervals(prev => {
      prev.forEach(interval => clearInterval(interval));
      return new Map();
    });

    // Start polling for any in-progress estimations
    const inProgressEstimations = estimations.filter(
      est => est.status === 'queued' || est.status === 'in_progress' || est.status === 'incomplete'
    );

    console.log('Found in-progress estimations:', inProgressEstimations);

    inProgressEstimations.forEach(estimation => {
      console.log(`Starting polling for estimation ${estimation._id} with status ${estimation.status}`);

      const pollInterval = setInterval(async () => {
        try {
          console.log(`Polling status for ${estimation._id}...`);
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/price-estimation/status/${estimation._id}`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'X-CSRF-TOKEN': getCookie('csrf_access_token'),
              }
            }
          );

          if (!response.ok) {
            console.error(`Failed to poll status for ${estimation._id}`);
            return;
          }

          const data = await response.json();
          console.log(`Poll result for ${estimation._id}:`, data);

          // If completed or failed, stop polling and refresh the list
          if (data.status === 'completed' || data.status === 'failed' || data.status === 'incomplete') {
            console.log(`Estimation ${estimation._id} is ${data.status}, stopping polling`);
            clearInterval(pollInterval);
            setPollingIntervals(prev => {
              const newMap = new Map(prev);
              newMap.delete(estimation._id);
              return newMap;
            });

            // Refresh the estimations list to show updated status
            fetchEstimations();
          }
        } catch (err) {
          console.error(`Error polling estimation ${estimation._id}:`, err);
        }
      }, 10000); // Poll every 10 seconds (webhooks handle the heavy lifting)

      setPollingIntervals(prev => new Map(prev).set(estimation._id, pollInterval));
    });

    // Cleanup on unmount
    return () => {
      setPollingIntervals(prev => {
        prev.forEach(interval => clearInterval(interval));
        return new Map();
      });
    };
  }, [estimations]); // Only depend on estimations

  const startPollingForEstimation = (estimationId: string) => {
    // This function is no longer used but kept for potential future use
    console.log('startPollingForEstimation called but not used in current implementation');
  };

  const fetchEstimations = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '3'
      });

      if (filterStatus) {
        params.append('status', filterStatus);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/price-estimation/history?${params}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          if (onBack) {
            onBack();
          } else {
            history.push('/login');
          }
          return;
        }
        throw new Error('Failed to fetch estimations');
      }

      const data = await response.json();
      // TODO: For test purposes, set status to in_progress for the estimation that is first in the list
      /*       if (data.estimations.length > 0 && page === 1) {
              data.estimations[0].status = 'in_progress';
            } */
      setEstimations(data.estimations);
      setTotalPages(data.pagination.pages);

    } catch (err) {
      console.error('Error fetching estimations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load estimations');
    } finally {
      setLoading(false);
    }
  };

  const fetchFullReport = async (estimationId: string) => {
    try {
      setLoadingReport(estimationId);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/price-estimation/report/${estimationId}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const data = await response.json();
      setSelectedEstimation(data);
      setReportModalOpen(true);
      onOpenEstimationDetail?.(estimationId);

    } catch (err) {
      console.error('Error fetching report:', err);
      alert(t('priceEstimations.errorLoadingReport'));
    } finally {
      setLoadingReport(null);
    }
  };

  const formatAddress = (address: EstimationListItem['address']) => {
    const parts = [];

    if (address.street) {
      let street = address.street;
      if (address.houseNumber) {
        street += ` ${address.houseNumber}`;
      }
      parts.push(street);
    }

    if (address.city) parts.push(address.city);
    if (address.postcode) parts.push(address.postcode);

    return parts.join(', ') || t('priceEstimations.unknownAddress');
  };

  const formatPrice = (price?: number) => {
    if (!price) return '-';
    return formatCardPrice(price);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isTimeoutError = (message?: string) => {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return normalized.includes('timed out') || normalized.includes('timeout');
  };

  const getStatusBadge = (status: EstimationListItem['status'], errorMessage?: string) => {
    const statusClasses = {
      'queued': 'price-estimations__status--queued',
      'in_progress': 'price-estimations__status--progress',
      'incomplete': 'price-estimations__status--progress',
      'completed': 'price-estimations__status--completed',
      'failed': 'price-estimations__status--failed'
    };

    const isTimedOut = status === 'failed' && isTimeoutError(errorMessage);
    const statusLabels = {
      'queued': t('priceEstimations.statusQueued'),
      'in_progress': t('priceEstimations.statusInProgress'),
      'incomplete': t('priceEstimations.statusInProgress'),
      'completed': t('priceEstimations.statusCompleted'),
      'failed': isTimedOut ? t('priceEstimations.statusTimedOut') : t('priceEstimations.statusFailed')
    };

    return (
      <span className={`price-estimations__status ${statusClasses[status]}`}>
        {statusLabels[status]}
      </span>
    );
  };

  if (loading && estimations.length === 0) {
    return (
      <div className={`price-estimations price-estimations--loading ${onBack ? 'price-estimations--embedded' : ''}`}>
        {onBack && !hideHeader && (
          <div className="price-estimations__back-header">
            <button
              className="price-estimations__back-button"
              onClick={() => {
                onCloseEstimationDetail?.();
                onBack?.();
              }}
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <h1 className="price-estimations__back-header__title">
              {t('priceEstimations.pageTitle')}
            </h1>
          </div>
        )}
        <div className="price-estimations__spinner"></div>
        <p>{t('priceEstimations.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`price-estimations ${onBack ? 'price-estimations--embedded' : ''}`}>
      {hideHeader ? null : onBack ? (
        <div className="price-estimations__back-header">
          <button
            className="price-estimations__back-button"
            onClick={() => {
              onCloseEstimationDetail?.();
              onBack?.();
            }}
            aria-label="Back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="price-estimations__back-header__title">
            {t('priceEstimations.pageTitle')}
          </h1>
        </div>
      ) : (
        <>
          <div className="price-estimations__logo-container">
            <img src="/assets/jaakko_head.png" alt="Jaakko" className="price-estimations__logo" />
          </div>
          <div className="price-estimations__header">
            <h1 className="price-estimations__title">
              {t('priceEstimations.pageTitle')}
            </h1>
          </div>
        </>
      )}

      {!onBack && (
        <div className="price-estimations__filters">
          <select
            className="price-estimations__filter-select"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('priceEstimations.allStatuses')}</option>
            <option value="completed">{t('priceEstimations.statusCompleted')}</option>
            <option value="in_progress">{t('priceEstimations.statusInProgress')}</option>
            <option value="queued">{t('priceEstimations.statusQueued')}</option>
            <option value="failed">{t('priceEstimations.statusFailed')}</option>
          </select>
        </div>
      )}

      {error && (
        <div className="price-estimations__error">
          <p>{error}</p>
          <button onClick={fetchEstimations}>{t('common.retry')}</button>
        </div>
      )}

      {estimations.length === 0 ? (
        <div className="price-estimations__empty">
          <div className="price-estimations__empty-icon">
          <svg
            version="1.1"
            id="fi_609803"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            x="0px"
            y="0px"
            viewBox="0 0 512 512"
            enableBackground="new 0 0 512 512"
            xmlSpace="preserve"
            width="64"
            height="64"
          >
            <rect x="382.933" y="73.6" fill="#F05540" width="58.667" height="102.4" />
            <rect x="360.533" y="21.333" fill="#F3705A" width="103.467" height="55.467" />
            <path
              fill="#FFD15C"
              d="M44.8,276.267V512h418.133V276.267c-82.133-82.133-128-128-209.067-209.067l0,0 C172.8,149.333,126.933,195.2,44.8,276.267z"
            />
            <path
              fill="#F7B64C"
              d="M464,276.267c-82.133-82.133-128-128-209.067-209.067c-41.6,40.533-73.6,72.533-105.6,104.533 s-64,64-104.533,104.533v48c82.133-82.133,128-128,209.067-209.067c82.133,82.133,128,128,209.067,209.067v6.4l0,0v-54.4H464z"
            />
            <path
              fill="#415A6B"
              d="M216.533,512H90.667V388.267c0-35.2,28.8-62.933,62.933-62.933l0,0c35.2,0,62.933,28.8,62.933,62.933 V512z"
            />
            <rect x="253.867" y="325.333" fill="#344A5E" width="164.267" height="144" />
            <g>
              <rect x="270.933" y="342.4" fill="#8AD7F8" width="56.533" height="109.867" />
              <rect x="344.533" y="342.4" fill="#8AD7F8" width="56.533" height="109.867" />
            </g>
            <path
              fill="#F3705A"
              d="M502.4,232.533L279.467,9.6c-12.8-12.8-35.2-12.8-48,0L9.6,232.533c-12.8,12.8-12.8,35.2,0,48 c12.8,12.8,35.2,12.8,48,0L256,82.133l198.4,198.4c12.8,12.8,35.2,12.8,48,0C515.2,266.667,515.2,245.333,502.4,232.533z"
            />
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
          </svg>

          <svg id="fi_7460812" enableBackground="new 0 0 68 68" height="64" viewBox="0 0 68 68" width="64" xmlns="http://www.w3.org/2000/svg"><g><path d="m42.1989059 2.1357574h-16.3990039c-2.0179977 0-3.2809982 2.1860046-2.2710037 3.934021l5.737999 9.940979h9.4650059l5.7389984-9.940979c1.0110015-1.7480163-.251999-3.934021-2.2719957-3.934021" fill="#febb50"></path><path d="m39.9070053 20.6392365h-11.8130054c-11.685997 3.6010131-20.0499993 13.2540283-20.0499993 24.5990295 0 .4659729.0139999.9289856.0420036 1.3889771.6609993 10.9200134 10.125 19.2369995 21.0649948 19.2369995h9.698c10.939003 0 20.4029999-8.3169861 21.0650024-19.2369995.0279999-.4599915.0419998-.9230042.0419998-1.3889771.0000001-11.3450012-8.3649978-20.9970093-20.0489959-24.5990295" fill="#febb50"></path><path d="m41.207695 51.2898407c-.586998.8880005-1.4219971 1.5970154-2.5049973 2.1270142-1.0830002.5289917-2.3379974.8370056-3.762001.9219971l.0049973 2.1990051c.0009995.2689819-.086998.4909973-.260994.6669922-.1750031.1749878-.4090042.2640076-.6970062.2640076-.2679977.0010071-.4899979-.0800171-.6660004-.2449951-.1769943-.1660156-.2639999-.3930054-.2649994-.6809998l-.0049973-2.2000122c-2.2310047-.1400146-4.3190022-.618988-6.260004-1.4410095-.2480011-.1029968-.4559937-.2729797-.621994-.5090027-.1650009-.2379761-.2490005-.4899902-.25-.7599792 0-.3500061.1179962-.6540222.3549957-.9129944.2360001-.2590027.5410004-.3900146.913002-.3910217.2070007 0 .4020004.0410156.5889969.1239929 1.6740036.7390137 3.4300003 1.1480103 5.2690067 1.2270203l-.0160065-7.125c-1.4060001-.3690186-2.5889988-.7680054-3.5499973-1.2000122-.9609985-.4309998-1.7630005-1.0449829-2.4049988-1.8379822-.6420059-.7929993-.965004-1.8000183-.9680023-3.0180054-.0019989-1.072998.2590027-2.0660095.7839966-2.9750061.5250015-.9110107 1.3070068-1.6659851 2.3490067-2.2669983s2.2949982-.9660034 3.7619953-1.0929871l-.0049973-2.1690063c-.0009995-.2669983.0859985-.4949951.262001-.6809998.1750031-.1870117.3959961-.2799988.6640015-.2799988.2689972-.0010071.4959946.0910034.6829987.2769775.185997.1849976.2799988.4130249.2799988.6800232l.0049973 2.1380005c2.0230026.0780029 3.8210068.414978 5.3920059 1.0099792.5789948.2050171.8689957.6180115.8699951 1.2369995.0009995.3310242-.1119995.6300049-.3379974.8990173-.2269974.2689819-.526001.4029846-.8980026.4039917-.1230011 0-.2890015-.0390015-.4959946-.1220093-1.4050064-.4920044-2.913002-.7669983-4.5240021-.8249817l.0169983 7.3719788c1.487999.3470154 2.7210007.7260132 3.7040024 1.1370239.9819946.4119873 1.8040009 1.0229797 2.4679947 1.8369751.6620026.8160095.9940033 1.8940125.9980011 3.2350159.0019989 1.0950013-.2889939 2.0870057-.8759995 2.9769898m-11.2390003-10.9100037c.6930008.4940186 1.7109985.9160156 3.0540028 1.2630005l-.0149994-6.8150024c-1.2390003.1690063-2.2300053.5470276-2.9710026 1.1370239-.7419968.5899963-1.112999 1.3609924-1.1100006 2.3110046.0019989.9079895.348999 1.6089783 1.0419998 2.1039734m9.3260059 7.7539978c-.0020065-.8049927-.3700027-1.4289856-1.1040039-1.8719788-.7340012-.4420166-1.8239975-.8580017-3.2709961-1.2470093l.0159988 6.6610107c2.9109955-.2130126 4.3639985-1.3940123 4.3590012-3.5420226" fill="#fff"></path><path d="m44.7400055 18.3320465c0 .8170166-.3290024 1.5620117-.8660049 2.0980225-.5370026.5369873-1.2929993.8659973-2.1100006.8659973h-15.5410023c-1.6459961 0-2.9629974-1.32901-2.9629974-2.9640198 0-.8169861.3290024-1.572998.8659973-2.1099854.5360031-.5369873 1.2810059-.8659973 2.0970001-.8659973h15.5410023c1.6460037 0 2.9760055 1.3299866 2.9760055 2.9759827" fill="#c17e48"></path></g></svg>
          
          </div>
          <h2>{t('priceEstimations.noEstimations')}</h2>
          <p>{t('priceEstimations.noEstimationsDesc')}</p>
        </div>
      ) : (
        <>
          <div className="price-estimations__list">
            {estimations.map(estimation => (
              <div
                key={estimation._id}
                className={`price-estimations__item ${estimation.status === 'in_progress' || estimation.status === 'queued' || estimation.status === 'incomplete'
                  ? 'price-estimations__item--processing'
                  : ''
                  }`}
                onClick={() => {
                  if (estimation.status === 'completed') {
                    fetchFullReport(estimation._id);
                  }
                }}
              >
                <div className="price-estimations__item-header">
                  <div className="price-estimations__item-address">
                    {formatAddress(estimation.address)}
                  </div>
                  {estimation.status !== 'completed' && getStatusBadge(estimation.status, estimation.error_message)}
                </div>

                <div className="price-estimations__item-details">
                  <div className="price-estimations__item-property">
                    {estimation.property_details?.size && (
                      <span>{estimation.property_details.size} m²</span>
                    )}
                    {estimation.property_details?.rooms && (
                      <span>{estimation.property_details.rooms} {t('priceEstimations.rooms')}</span>
                    )}
                  </div>

                  {/*                   {estimation.status === 'completed' && estimation.result && (
                    <div className="price-estimations__item-price">
                      <strong>{formatPrice(estimation.result.estimated_price)}</strong>
                      {estimation.result.confidence_level && (
                        <span className="price-estimations__item-confidence">
                          ({estimation.result.confidence_level} {t('priceEstimations.confidence')})
                        </span>
                      )}
                    </div>
                  )} */}
                </div>

                <div className="price-estimations__item-footer">
                  <span className="price-estimations__item-date">
                    {t('priceEstimations.created')}: {formatDate(estimation.created_at)}
                  </span>
                  {/*                   {estimation.completed_at && (
                    <span className="price-estimations__item-date">
                      {t('priceEstimations.completed')}: {formatDate(estimation.completed_at)}
                    </span>
                  )} */}
                </div>
                {estimation.status === 'failed' && (
                  <div className="price-estimations__item-error">
                    {isTimeoutError(estimation.error_message)
                      ? t('priceEstimations.timedOutMessage')
                      : t('priceEstimations.failedMessage')}
                  </div>
                )}

                {estimation.status === 'completed' && (
                  <button
                    className="price-estimations__item-view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchFullReport(estimation._id);
                    }}
                    disabled={loadingReport === estimation._id}
                  >
                    {loadingReport === estimation._id ? t('common.loading') : t('priceEstimations.viewReport')}
                  </button>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="price-estimations__pagination">
              <button
                className="price-estimations__pagination-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {t('common.previous')}
              </button>

              <div className="price-estimations__pagination-info">
                <span>{t('priceEstimations.pageInfo')}</span>
                <span>{page} / {totalPages}</span>
              </div>

              <button
                className="price-estimations__pagination-btn"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}

      {selectedEstimation && (
        <PriceEstimationReport
          isOpen={reportModalOpen}
          onClose={() => {
            setReportModalOpen(false);
            setSelectedEstimation(null);
            onCloseEstimationDetail?.();
          }}
          estimation={selectedEstimation}
        />
      )}
    </div>
  );
};

export default PriceEstimations;
